import {
  QueryClient,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";
import type {
  AggregateResponse,
  GroupPayload,
  SchemaResponse,
  TestResult,
} from "./types";

export const settingsQueryKey = (group: string) =>
  ["settings", group] as const;

export const endpointQueryKey = (url: string) => ["endpoint", url] as const;

async function getJSON<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) throw new Error(`${url}: ${r.status}`);
  return r.json() as Promise<T>;
}

/** Primary hook — fetches a single group's payload. */
export function useSettings(
  group: string,
): UseQueryResult<GroupPayload, Error> {
  return useQuery({
    queryKey: settingsQueryKey(group),
    queryFn: () => getJSON<GroupPayload>(`/api/admin/settings/${group}`),
  });
}

/** Fetches an arbitrary endpoint. Used by readonly/custom panels AND by
 *  VersionBadge — so sharing `['endpoint', '/api/version']` key guarantees
 *  header+About see the same data from one network call. */
export function useEndpointData<T>(endpoint: string): UseQueryResult<T, Error> {
  return useQuery({
    queryKey: endpointQueryKey(endpoint),
    queryFn: () => getJSON<T>(endpoint),
  });
}

export interface VersionInfo {
  version: string;
  commit: string;
  commit_short: string;
  built_at: string;
  go_version: string;
  saas_common_version?: string;
  admin_design_version?: string;
  db_schema_version?: {
    applied: number;
    available: number;
    drift: boolean;
  };
}

export function useVersion() {
  return useEndpointData<VersionInfo>("/api/version");
}

/** Bootstrap: one aggregate call seeds every per-group cache. Aggregate itself
 *  is not cached — callers read through useSettings from this point on. */
export async function prefetchAllSettings(qc: QueryClient): Promise<void> {
  const res = await getJSON<AggregateResponse>("/api/admin/settings");
  for (const [key, payload] of Object.entries(res.groups)) {
    qc.setQueryData(settingsQueryKey(key), payload);
  }
}

export async function fetchSchema(): Promise<SchemaResponse> {
  return getJSON<SchemaResponse>("/api/admin/settings/schema");
}

export async function putGroup(
  group: string,
  body: Record<string, unknown>,
): Promise<GroupPayload> {
  const r = await fetch(`/api/admin/settings/${group}`, {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) throw new Error(await r.text());
  return (await r.json()) as GroupPayload;
}

export async function testGroup(group: string): Promise<TestResult> {
  const r = await fetch(`/api/admin/settings/${group}/test`, {
    method: "POST",
    credentials: "include",
  });
  return (await r.json()) as TestResult;
}

export async function createRow(
  group: string,
  body: { id?: string; fields: Record<string, unknown> },
): Promise<{ group: string; kind: "collection"; row: { id: string; fields: Record<string, unknown> } }> {
  const r = await fetch(`/api/admin/settings/${group}/rows`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const text = await r.text();
    if (r.status === 409) throw new Error("id_taken");
    throw new Error(`${r.status}: ${text}`);
  }
  return r.json();
}

export async function deleteRow(group: string, id: string): Promise<void> {
  const r = await fetch(`/api/admin/settings/${group}/rows/${id}`, {
    method: "DELETE",
    credentials: "include",
  });
  if (!r.ok && r.status !== 204) throw new Error(await r.text());
}
